import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Container, Form, Nav, ProgressBar, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

import {
  createCompany,
  createUserProfile,
  geCompanyData,
  getPaymentStatus,
  getProfileData,
  getSubscribedUser,
  getUserData,
  sendDocToS3,
  updateCompany,
  updateUserImageProfile,
  updateUserProfile,
} from "slices/thunk";
import "./modern-profile.css";

interface JwtPayload {
  userId?: string;
  userType?: string;
  user_type?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

type PersonalDraft = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  descpription: string;
  address: string;
  cityInput: string;
  userState: string;
  zipcodeInput: string;
  countryInput: string;
  birthDate: string;
  joiningDate: string;
  experience: string;
  workHours: string;
  skills: string[];
  workAvailability: string[];
  profile: string[];
};

type BusinessDraft = {
  companyName: string;
  companyRegisterdNumber: string;
  companyPhoneNumber: string;
  companyEmail: string;
  companyRegisterdDate: string;
  companyAddress: string;
  companyCity: string;
  companyState: string;
  companyZip: string;
  companyCountry: string;
};

const emptyPersonalDraft: PersonalDraft = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  descpription: "",
  address: "",
  cityInput: "",
  userState: "",
  zipcodeInput: "",
  countryInput: "",
  birthDate: "",
  joiningDate: "",
  experience: "",
  workHours: "",
  skills: [],
  workAvailability: [],
  profile: [],
};

const emptyBusinessDraft: BusinessDraft = {
  companyName: "",
  companyRegisterdNumber: "",
  companyPhoneNumber: "",
  companyEmail: "",
  companyRegisterdDate: "",
  companyAddress: "",
  companyCity: "",
  companyState: "",
  companyZip: "",
  companyCountry: "",
};

const getStoredAuthToken = () => {
  const authUser = localStorage.getItem("authUser");

  if (!authUser) return null;

  try {
    return JSON.parse(authUser);
  } catch (error) {
    return authUser.replace(/^"|"$/g, "");
  }
};

const decodeToken = (): JwtPayload => {
  const token = getStoredAuthToken();

  if (!token) return {};

  try {
    return jwtDecode<JwtPayload>(token);
  } catch (error) {
    return {};
  }
};

const parseList = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (error) {
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
};

const toDateInput = (value: any) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

const toProperLabel = (value?: string) =>
  (value || "user")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const buildPersonalDraft = (userData: any, user: any, decoded: JwtPayload): PersonalDraft => ({
  firstName: userData?.firstName || user?.user_first_name || decoded?.firstName || "",
  lastName: userData?.lastName || user?.user_last_name || decoded?.lastName || "",
  phoneNumber: userData?.ua_phone_number || user?.user_phone_number || "",
  descpription: userData?.ua_description || "",
  address: userData?.ua_address || "",
  cityInput: userData?.ua_city || "",
  userState: userData?.ua_state || "",
  zipcodeInput: userData?.ua_zip_code || "",
  countryInput: userData?.ua_country || "",
  birthDate: toDateInput(userData?.ua_birth_date),
  joiningDate: toDateInput(userData?.ua_joining_date),
  experience: userData?.ua_experience || "",
  workHours: userData?.ua_work_hours || "",
  skills: parseList(userData?.ua_skills),
  workAvailability: parseList(userData?.ua_work_availability),
  profile: parseList(userData?.ua_profile),
});

const buildBusinessDraft = (company: any, user: any): BusinessDraft => ({
  companyName: company?.cc_company_name || "",
  companyRegisterdNumber: company?.cc_company_registered_number || "",
  companyPhoneNumber: company?.cc_phone_number || "",
  companyEmail: company?.cc_company_email || user?.user_email || "",
  companyRegisterdDate: toDateInput(company?.cc_registration_date),
  companyAddress: company?.cc_address || "",
  companyCity: company?.cc_city || "",
  companyState: company?.cc_state || "",
  companyZip: company?.cc_zip_code || "",
  companyCountry: company?.cc_country || "",
});

const ModernProfile = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const decoded = useMemo(() => decodeToken(), []);
  const userType = decoded?.userType || decoded?.user_type || "user";
  const isAgentUser = userType === "real_estate_agent" || userType === "broker";
  const isBusinessUser = userType === "contractor" || userType === "testContractor";

  const userData = useSelector((state: any) => state.UserProfile.userData);
  const userRows = useSelector((state: any) => state.UserProfile.user);
  const company = useSelector((state: any) => state.ContractorCompany.company);
  const paymentVerified = useSelector((state: any) => state.Payment.paymentStatus);
  const subscribedData = useSelector((state: any) => state.UserProfile.subscribedData);

  const user = Array.isArray(userRows) ? userRows[0] : userRows;
  const [activeSection, setActiveSection] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState("");
  const [personalDraft, setPersonalDraft] = useState<PersonalDraft>(emptyPersonalDraft);
  const [businessDraft, setBusinessDraft] = useState<BusinessDraft>(emptyBusinessDraft);

  document.title = "My Profile";

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(getProfileData()),
      dispatch(getUserData()),
      isBusinessUser ? dispatch(getPaymentStatus()) : Promise.resolve(),
      isBusinessUser ? dispatch(getSubscribedUser()) : Promise.resolve(),
      isBusinessUser ? dispatch(geCompanyData()) : Promise.resolve(),
    ]).finally(() => setLoading(false));
  }, [dispatch, isBusinessUser]);

  useEffect(() => {
    setPersonalDraft(buildPersonalDraft(userData, user, decoded));
  }, [decoded, user, userData]);

  useEffect(() => {
    setBusinessDraft(buildBusinessDraft(company, user));
  }, [company, user]);

  const displayName =
    [personalDraft.firstName, personalDraft.lastName].filter(Boolean).join(" ") ||
    user?.user_name ||
    user?.user_email ||
    "Your profile";
  const profileImage = userData?.ua_profile_pic || user?.ua_profile_pic;
  const coverImage = userData?.ua_cover_pic;
  const email = user?.user_email || decoded?.email || businessDraft.companyEmail || "Not added";

  const standardCompletedFields = [
    personalDraft.firstName,
    personalDraft.lastName,
    email !== "Not added" ? email : "",
    personalDraft.phoneNumber,
    personalDraft.address,
    personalDraft.cityInput,
    personalDraft.descpription,
    profileImage,
    isBusinessUser ? businessDraft.companyName : "homeowner",
    isBusinessUser ? businessDraft.companyPhoneNumber : "homeowner",
  ].filter(Boolean).length;
  const agentCompletedFields = [
    personalDraft.firstName,
    personalDraft.lastName,
    email !== "Not added" ? email : "",
    personalDraft.phoneNumber,
    personalDraft.address,
    personalDraft.cityInput,
    personalDraft.userState,
    personalDraft.zipcodeInput,
    personalDraft.countryInput,
  ].filter(Boolean).length;
  const profileCompletion = isAgentUser
    ? Math.round((agentCompletedFields / 9) * 100)
    : Math.round((standardCompletedFields / 10) * 100);

  const savePersonal = async (fieldName = "profile") => {
    setSavingField(fieldName);
    const cover = coverImage || "";
    const avatar = profileImage || "";
    const social = userData?.ua_social_url || "";

    try {
      if (userData) {
        await dispatch(updateUserProfile(personalDraft, cover, avatar, social));
      } else {
        await dispatch(createUserProfile(personalDraft, cover, avatar, social));
      }

      await Promise.all([dispatch(getProfileData()), dispatch(getUserData())]);
      toast.success("Profile updated");
    } catch (error) {
      toast.warning("Profile update failed");
    } finally {
      setSavingField("");
    }
  };

  const saveBusiness = async (fieldName = "business") => {
    if (!isBusinessUser) return;

    setSavingField(fieldName);

    try {
      if (company) {
        await dispatch(
          updateCompany(
            businessDraft,
            company?.cc_licence_documentation_url || "",
            company?.cc_contractor_documentation_url || "",
            company?.cc_insurance_documentation_url || "",
            company?.cc_video_url || "",
            company?.cc_logo_url || ""
          )
        );
      } else {
        await dispatch(createCompany(businessDraft, "", "", "", "", ""));
      }

      await dispatch(geCompanyData());
      toast.success("Business profile updated");
    } catch (error) {
      toast.warning("Business update failed");
    } finally {
      setSavingField("");
    }
  };

  const handleImageChange = async (type: "profile" | "cover", file?: File) => {
    if (!file) return;

    setSavingField(type);

    try {
      const image = await dispatch(sendDocToS3(type, file));
      await dispatch(updateUserImageProfile({ img: image, imgType: type === "profile" ? "profilePic" : "coverPic" }));
      await Promise.all([dispatch(getProfileData()), dispatch(getUserData())]);
      toast.success(type === "profile" ? "Profile photo updated" : "Cover photo updated");
    } catch (error) {
      toast.warning("Image update failed");
    } finally {
      setSavingField("");
    }
  };

  const handlePersonalChange = (field: keyof PersonalDraft, value: string) => {
    setPersonalDraft((current) => ({ ...current, [field]: value }));
  };

  const handleBusinessChange = (field: keyof BusinessDraft, value: string) => {
    setBusinessDraft((current) => ({ ...current, [field]: value }));
  };

  return (
    <div className="page-content modern-profile-page">
      <Container fluid>
        <div className="modern-profile-shell">
          <div
            className="modern-profile-hero"
            style={coverImage ? { backgroundImage: `linear-gradient(90deg, rgba(15,23,42,.86), rgba(15,23,42,.45)), url(${coverImage})` } : undefined}
          >
            <div className="modern-profile-avatar">
              {profileImage ? <img src={profileImage} alt={displayName} /> : <span>{displayName.slice(0, 1).toUpperCase()}</span>}
              <label className="modern-profile-avatar-action">
                <i className="bi bi-camera"></i>
                <input type="file" accept="image/*" onChange={(event) => handleImageChange("profile", event.target.files?.[0])} />
              </label>
            </div>

            <div className="modern-profile-hero-copy">
              <Badge bg="light" text="dark" className="modern-profile-role">
                {toProperLabel(userType)}
              </Badge>
              <h2>{displayName}</h2>
              <p>{email}</p>
              <div className="modern-profile-actions">
                <Button as={Link as any} to="/pages-profile-settings" variant="light">
                  <i className="bi bi-sliders me-1"></i> Old Settings
                </Button>
                <Button as={Link as any} to="/pages-pricing" variant="outline-light">
                  <i className="bi bi-credit-card me-1"></i> Old Pricing
                </Button>
                <label className="btn btn-outline-light mb-0">
                  <i className="bi bi-image me-1"></i> Cover
                  <input type="file" accept="image/*" className="d-none" onChange={(event) => handleImageChange("cover", event.target.files?.[0])} />
                </label>
              </div>
            </div>
          </div>

          <Row className="g-3 mt-1">
            <Col xl={3}>
              <div className="modern-profile-panel">
                <div className="modern-profile-score">
                  <div>
                    <span>Profile completion</span>
                    <strong>{profileCompletion}%</strong>
                  </div>
                  <ProgressBar now={profileCompletion} />
                </div>

                <Nav className="modern-profile-nav" activeKey={activeSection} onSelect={(key) => key && setActiveSection(key)}>
                  <Nav.Link eventKey="overview">
                    <i className="bi bi-grid"></i> Overview
                  </Nav.Link>
                  <Nav.Link eventKey="profile">
                    <i className="bi bi-person"></i> Profile details
                  </Nav.Link>
                  {isBusinessUser && (
                    <Nav.Link eventKey="business">
                      <i className="bi bi-building"></i> Business
                    </Nav.Link>
                  )}
                  <Nav.Link eventKey="billing">
                    <i className="bi bi-wallet2"></i> Pricing & settings
                  </Nav.Link>
                </Nav>

                <div className="modern-profile-mini">
                  <span>Payment</span>
                  <strong className={paymentVerified ? "text-success" : "text-warning"}>
                    {paymentVerified ? "Verified" : "Pending"}
                  </strong>
                </div>
              </div>
            </Col>

            <Col xl={9}>
              {loading ? (
                <div className="modern-profile-panel modern-profile-loading">
                  <Spinner animation="border" size="sm" />
                  <span>Loading profile...</span>
                </div>
              ) : (
                <>
                  {activeSection === "overview" && (
                    <div className="modern-profile-grid">
                      <div className="modern-profile-panel">
                        <h5>Account snapshot</h5>
                        <div className="modern-profile-stats">
                          <div>
                            <span>Role</span>
                            <strong>{toProperLabel(userType)}</strong>
                          </div>
                          <div>
                            <span>Phone</span>
                            <strong>{personalDraft.phoneNumber || "Not added"}</strong>
                          </div>
                          <div>
                            <span>Location</span>
                            <strong>{[personalDraft.cityInput, personalDraft.userState].filter(Boolean).join(", ") || "Not added"}</strong>
                          </div>
                        </div>
                      </div>

                      <div className="modern-profile-panel">
                        <h5>Quick decisions</h5>
                        <div className="modern-profile-shortcuts">
                          <button type="button" onClick={() => setActiveSection("profile")}>
                            <i className="bi bi-pencil-square"></i>
                            Edit profile in new flow
                          </button>
                          <Link to="/pages-profile-settings">
                            <i className="bi bi-box-arrow-up-right"></i>
                            Open old settings flow
                          </Link>
                          <Link to="/pages-pricing">
                            <i className="bi bi-tags"></i>
                            Compare old pricing flow
                          </Link>
                        </div>
                      </div>

                      <div className="modern-profile-panel modern-profile-wide">
                        <h5>About</h5>
                        <p>{personalDraft.descpription || "Add a short profile summary so customers understand who they are working with."}</p>
                      </div>
                    </div>
                  )}

                  {activeSection === "profile" && (
                    <div className="modern-profile-panel">
                      <div className="modern-profile-section-heading">
                        <div>
                          <h5>Profile details</h5>
                          <p>Fields auto-refresh the profile after save, so the header and profile cards stay current.</p>
                        </div>
                        <Button onClick={() => savePersonal("profile")} disabled={Boolean(savingField)}>
                          {savingField === "profile" ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check2 me-1"></i>}
                          Save profile
                        </Button>
                        {isAgentUser && (
                          <Button variant="outline-primary" onClick={() => navigate("/dashboard")}>
                            Finish & return to dashboard
                          </Button>
                        )}
                      </div>

                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Label>First name</Form.Label>
                          <Form.Control value={personalDraft.firstName} onChange={(event) => handlePersonalChange("firstName", event.target.value)} onBlur={() => savePersonal("firstName")} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Last name</Form.Label>
                          <Form.Control value={personalDraft.lastName} onChange={(event) => handlePersonalChange("lastName", event.target.value)} onBlur={() => savePersonal("lastName")} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Phone</Form.Label>
                          <Form.Control value={personalDraft.phoneNumber} onChange={(event) => handlePersonalChange("phoneNumber", event.target.value)} onBlur={() => savePersonal("phoneNumber")} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Experience</Form.Label>
                          <Form.Control value={personalDraft.experience} onChange={(event) => handlePersonalChange("experience", event.target.value)} onBlur={() => savePersonal("experience")} />
                        </Col>
                        <Col md={12}>
                          <Form.Label>Profile summary</Form.Label>
                          <Form.Control as="textarea" rows={4} value={personalDraft.descpription} onChange={(event) => handlePersonalChange("descpription", event.target.value)} onBlur={() => savePersonal("descpription")} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Address</Form.Label>
                          <Form.Control value={personalDraft.address} onChange={(event) => handlePersonalChange("address", event.target.value)} onBlur={() => savePersonal("address")} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>City</Form.Label>
                          <Form.Control value={personalDraft.cityInput} onChange={(event) => handlePersonalChange("cityInput", event.target.value)} onBlur={() => savePersonal("cityInput")} />
                        </Col>
                        <Col md={4}>
                          <Form.Label>State</Form.Label>
                          <Form.Control value={personalDraft.userState} onChange={(event) => handlePersonalChange("userState", event.target.value)} onBlur={() => savePersonal("userState")} />
                        </Col>
                        <Col md={4}>
                          <Form.Label>Zip code</Form.Label>
                          <Form.Control value={personalDraft.zipcodeInput} onChange={(event) => handlePersonalChange("zipcodeInput", event.target.value)} onBlur={() => savePersonal("zipcodeInput")} />
                        </Col>
                        <Col md={4}>
                          <Form.Label>Country</Form.Label>
                          <Form.Control value={personalDraft.countryInput} onChange={(event) => handlePersonalChange("countryInput", event.target.value)} onBlur={() => savePersonal("countryInput")} />
                        </Col>
                      </Row>
                    </div>
                  )}

                  {activeSection === "business" && isBusinessUser && (
                    <div className="modern-profile-panel">
                      <div className="modern-profile-section-heading">
                        <div>
                          <h5>Business profile</h5>
                          <p>A simpler surface for company identity. The old detailed settings page is still available.</p>
                        </div>
                        <Button onClick={() => saveBusiness("business")} disabled={Boolean(savingField)}>
                          {savingField === "business" ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check2 me-1"></i>}
                          Save business
                        </Button>
                      </div>

                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Label>Company name</Form.Label>
                          <Form.Control value={businessDraft.companyName} onChange={(event) => handleBusinessChange("companyName", event.target.value)} onBlur={() => saveBusiness("companyName")} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Registration number</Form.Label>
                          <Form.Control value={businessDraft.companyRegisterdNumber} onChange={(event) => handleBusinessChange("companyRegisterdNumber", event.target.value)} onBlur={() => saveBusiness("companyRegisterdNumber")} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Company phone</Form.Label>
                          <Form.Control value={businessDraft.companyPhoneNumber} onChange={(event) => handleBusinessChange("companyPhoneNumber", event.target.value)} onBlur={() => saveBusiness("companyPhoneNumber")} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Company email</Form.Label>
                          <Form.Control value={businessDraft.companyEmail} onChange={(event) => handleBusinessChange("companyEmail", event.target.value)} onBlur={() => saveBusiness("companyEmail")} />
                        </Col>
                        <Col md={12}>
                          <Form.Label>Business address</Form.Label>
                          <Form.Control value={businessDraft.companyAddress} onChange={(event) => handleBusinessChange("companyAddress", event.target.value)} onBlur={() => saveBusiness("companyAddress")} />
                        </Col>
                        <Col md={4}>
                          <Form.Label>City</Form.Label>
                          <Form.Control value={businessDraft.companyCity} onChange={(event) => handleBusinessChange("companyCity", event.target.value)} onBlur={() => saveBusiness("companyCity")} />
                        </Col>
                        <Col md={4}>
                          <Form.Label>State</Form.Label>
                          <Form.Control value={businessDraft.companyState} onChange={(event) => handleBusinessChange("companyState", event.target.value)} onBlur={() => saveBusiness("companyState")} />
                        </Col>
                        <Col md={4}>
                          <Form.Label>Zip code</Form.Label>
                          <Form.Control value={businessDraft.companyZip} onChange={(event) => handleBusinessChange("companyZip", event.target.value)} onBlur={() => saveBusiness("companyZip")} />
                        </Col>
                      </Row>
                    </div>
                  )}

                  {activeSection === "billing" && (
                    <div className="modern-profile-grid">
                      <div className="modern-profile-panel">
                        <h5>Pricing comparison</h5>
                        <p>Use this new hub to decide whether pricing should live inside profile or stay as its own old flow.</p>
                        <div className="modern-profile-shortcuts">
                          <Link to="/pages-pricing">
                            <i className="bi bi-box-arrow-up-right"></i>
                            Open existing pricing page
                          </Link>
                          <Link to="/contract-tutorial-and-plans">
                            <i className="bi bi-play-circle"></i>
                            Open tutorial and plans
                          </Link>
                        </div>
                      </div>

                      <div className="modern-profile-panel">
                        <h5>Settings comparison</h5>
                        <p>Old detailed settings remain untouched while this page gives a faster edit path.</p>
                        <div className="modern-profile-shortcuts">
                          <Link to="/pages-profile-settings">
                            <i className="bi bi-sliders"></i>
                            Open existing settings page
                          </Link>
                          <button type="button" onClick={() => setActiveSection("profile")}>
                            <i className="bi bi-person-gear"></i>
                            Edit in new profile flow
                          </button>
                        </div>
                      </div>

                      <div className="modern-profile-panel modern-profile-wide">
                        <h5>Current billing status</h5>
                        <div className="modern-profile-stats">
                          <div>
                            <span>Payment verified</span>
                            <strong>{paymentVerified ? "Yes" : "No"}</strong>
                          </div>
                          <div>
                            <span>Provider</span>
                            <strong>{subscribedData?.paymentProvider || "Not available"}</strong>
                          </div>
                          <div>
                            <span>Plan</span>
                            <strong>{subscribedData?.package || subscribedData?.plan || "Not available"}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default ModernProfile;
